/**
 * 奈良県の背景ツアー「奈良めぐり」コンテンツ。
 *
 * 軸は一日の時間と季節。朝の奈良公園から、秋の明日香へ。
 * 奈良市内は3か所にとどめ、斑鳩・橿原・十津川・吉野・宇陀・曽爾・明日香に7か所を配している。
 *
 * 奈良公園のシカについて。
 *  国の天然記念物で、飼われている動物ではなく野生動物。
 *  餌やりや接触を勧める書き方をしない。絵にも、手から食べさせる構図や抱く構図を出さない。
 *  与えてよいのは鹿せんべいだけで、じらさない、ごみは持ち帰る、という注意を必ず添える。
 *
 * 仏像について。
 *  東大寺の大仏をはじめ、堂内の仏像は絵に描かせない。建物の外観・舞台・回廊・塔だけを主題にする。
 *  高松塚古墳・キトラ古墳の壁画も描かせない。
 *
 * 「日本一」「最古」の扱い。限定語ごと引用し、短縮しない。
 *  - 谷瀬の吊り橋は「日本一長い生活用鉄線吊り橋」（十津川村公式）。「日本一長い吊り橋」と書かない。
 *  - 室生寺は「屋外に立つ古塔としてはわが国最小の五重塔」（県観光公式）。
 *  - 東大寺大仏殿は「世界最大規模の木造建築物」（奈良市公式）。「世界最大」と書かない。
 *  - 今井町は「重伝建地区のなかで全国最多」（橿原市公式）。
 *  - 法隆寺の「現存する世界最古の木造建築群」は県の観光公式の表現。出典を明示して書く。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 谷瀬の吊り橋の長さ・高さの数値は十津川村公式で確認できなかった。書かない。
 *  - 石舞台古墳の特別史跡指定、稲渕の棚田百選も未確認。書かない。
 *  - 奈良県の公式キャラクターは出さない。
 *  - お水取りの日程、拝観料、山焼きの実施日など時間で変わるものは書かない。
 */

import type { StageTour } from '../types';

export const naraSceneryTour: StageTour = {
  scenes: [
    {
      id: 'nara-koen-shika',
      name: '奈良公園のシカ',
      area: '奈良市・早朝',
      image: '/assets/scenery/nara/scenes/01_nara_koen_shika.webp',
      hotspots: [
        {
          id: 'shika-tennen',
          x: 0.46,
          y: 0.44,
          title: '国の天然記念物のシカ',
          body: '奈良公園には約1400頭の野生のシカが暮らしており、国の天然記念物に指定されています。飼われている動物ではなく野生動物なので、触ろうとしてむやみに驚かせたりしないでください。',
          source: 'https://narashikanko.or.jp/spot/detail_10089.html',
          mapQuery: '奈良公園',
        },
        {
          id: 'shika-senbei',
          x: 0.74,
          y: 0.34,
          title: '鹿せんべい以外は与えない',
          body: '与えてよいのは鹿せんべいだけで、それ以外の食べものを与えてはいけません。持ったままじらしたり驚かせたりすると、思わぬ行動に出ることがあります。公園にごみ箱はなく、誤食を防ぐためごみはすべて持ち帰ります。',
          source: 'https://narashikanko.or.jp/spot/detail_10089.html',
          mapQuery: '奈良公園 鹿',
        },
        {
          id: 'shika-511ha',
          x: 0.22,
          y: 0.5,
          title: '511ヘクタールの公園',
          body: '奈良公園は奈良市街の東側に広がる約511ヘクタールの公園で、東大寺・興福寺・春日大社や春日山原始林までを含みます。美しい芝生と樹齢千年をこえる松の林が、古都らしい景色をつくっています。',
          source: 'https://narashikanko.or.jp/spot/detail_10089.html',
          mapQuery: '奈良公園 芝生',
        },
      ],
    },
    {
      id: 'horyuji',
      name: '法隆寺',
      area: '斑鳩町・午前',
      image: '/assets/scenery/nara/scenes/02_horyuji.webp',
      hotspots: [
        {
          id: 'horyuji-1993',
          x: 0.46,
          y: 0.4,
          title: '1993年の世界遺産',
          body: '「法隆寺地域の仏教建造物」は、平成5年（1993年）12月11日に世界文化遺産に登録されました。姫路城とともに、日本で初めて登録された世界文化遺産です。',
          source: 'https://www.city.himeji.lg.jp/kanko/0000001645.html',
          mapQuery: '法隆寺',
        },
        {
          id: 'horyuji-55mune',
          x: 0.74,
          y: 0.34,
          title: '国宝・重文が55棟という規模',
          body: '境内には国宝または重要文化財に指定された建造物が55棟あり、文化財の総数はおよそ3000点にのぼるとされます。寺伝では推古天皇と聖徳太子により607年に建てられたと伝えられています。',
          source: 'https://yamatoji.nara-kankou.or.jp/01shaji/02tera/02west_area/horyuji/',
          mapQuery: '法隆寺 西院伽藍',
        },
        {
          id: 'horyuji-saiko',
          x: 0.22,
          y: 0.5,
          title: '県の観光公式が記す古さ',
          body: '奈良県の観光公式サイトは、法隆寺を「現存する世界最古の木造建築群として往時の姿を今に伝えている」と紹介しています。いまの伽藍は8世紀初頭に完成したと考えられています。',
          source: 'https://yamatoji.nara-kankou.or.jp/01shaji/02tera/02west_area/horyuji/',
          mapQuery: '法隆寺 五重塔',
        },
      ],
    },
    {
      id: 'imaicho',
      name: '今井町',
      area: '橿原市・昼前',
      image: '/assets/scenery/nara/scenes/03_imaicho.webp',
      hotspots: [
        {
          id: 'imaicho-saita',
          x: 0.46,
          y: 0.42,
          title: '全国で最も多い伝統建造物',
          body: '今井町は東西およそ600メートル、南北およそ310メートル、17.4ヘクタールの範囲におよそ760棟の建物が建ち、うち約500棟が伝統的建造物です。橿原市は、重要伝統的建造物群保存地区のなかで全国最多と説明しています。',
          source: 'https://www.city.kashihara.nara.jp/soshiki/1063/gyomu/1/2/1/3830.html',
          mapQuery: '今井町',
        },
        {
          id: 'imaicho-1993',
          x: 0.74,
          y: 0.34,
          title: '1993年選定の寺内町',
          body: '文化庁の一覧では、橿原市今井町は平成5年12月8日に「寺内町・在郷町」として重要伝統的建造物群保存地区に選定されています。面積は17.4ヘクタールです。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '今井町 称念寺',
        },
        {
          id: 'imaicho-shichibu',
          x: 0.22,
          y: 0.5,
          title: '「大和の金は今井に七分」',
          body: '商業で栄えた今井には「大和の金は今井に七分」という言葉が生まれたと伝わります。国の重要文化財の民家が9件あり、室町時代末期にできた町割りがそのまま現在まで残っています。',
          source: 'https://www.city.kashihara.nara.jp/soshiki/1063/gyomu/1/2/1/3830.html',
          mapQuery: '今井町 今西家住宅',
        },
      ],
    },
    {
      id: 'tanize-tsuribashi',
      name: '谷瀬の吊り橋',
      area: '十津川村・昼',
      image: '/assets/scenery/nara/scenes/04_tanize_tsuribashi.webp',
      hotspots: [
        {
          id: 'tanize-seikatsuyo',
          x: 0.46,
          y: 0.42,
          title: '長さ297メートルの生活用吊り橋',
          body: '十津川村は谷瀬の吊り橋を、生活用鉄線吊り橋としては長さ日本一と紹介しています。長さは297メートル、高さは54メートルにもなります。',
          source: 'https://www.vill.totsukawa.lg.jp/traveling_guide/spot/view/',
          mapQuery: '谷瀬の吊り橋',
        },
        {
          id: 'tanize-kuchu',
          x: 0.74,
          y: 0.34,
          title: '空中散歩のような渡り',
          body: '周囲はそびえ立つ山々に囲まれ、眼下には清澄な熊野川（十津川）が流れています。十津川村はこの吊り橋を、まるで空中を散歩している気分になれる場所と紹介しています。',
          source: 'https://www.vill.totsukawa.lg.jp/traveling_guide/spot/view/',
          mapQuery: '谷瀬の吊り橋 上野地',
        },
        {
          id: 'tanize-sekaiisan',
          x: 0.22,
          y: 0.5,
          title: '世界遺産の道がある村',
          body: '十津川村には、熊野本宮大社へ続く世界遺産「小辺路」沿いの果無集落や、切り立った断崖の大峡谷・瀞峡、熊野三山の奥の宮とされる玉置神社があり、吊り橋とあわせて山深い村の姿を伝えています。',
          source: 'https://www.vill.totsukawa.lg.jp/traveling_guide/spot/',
          mapQuery: '十津川村 果無集落',
        },
      ],
    },
    {
      id: 'todaiji-nigatsudo',
      name: '東大寺 二月堂',
      area: '奈良市・夕暮れ',
      image: '/assets/scenery/nara/scenes/05_todaiji_nigatsudo.webp',
      hotspots: [
        {
          id: 'todaiji-saidaikibo',
          x: 0.46,
          y: 0.4,
          title: '世界最大規模とされる木造建築物',
          body: '奈良市は東大寺大仏殿を「世界最大規模の木造建築物」と紹介しています。現在の建物は1709年に再建されたもので、最初の大仏殿は751年に完成し、翌752年に開眼の法要が行われました。',
          source: 'https://www.city.nara.lg.jp/site/world-heritage/88515.html',
          mapQuery: '東大寺 大仏殿',
        },
        {
          id: 'todaiji-9mune',
          x: 0.74,
          y: 0.5,
          title: '国宝の建物が9棟',
          body: '東大寺には南大門、法華堂、鐘楼、大仏殿、開山堂、転害門、本坊経庫、正倉院正倉、二月堂の9棟の国宝建造物があります。南大門は鎌倉時代、二月堂は江戸時代の再建です。',
          source: 'https://www.city.nara.lg.jp/site/world-heritage/88515.html',
          mapQuery: '東大寺 二月堂',
        },
        {
          id: 'todaiji-1998',
          x: 0.22,
          y: 0.48,
          title: '1998年の世界遺産',
          body: '東大寺は世界遺産「古都奈良の文化財」を構成する8資産のひとつで、平成10年（1998年）12月に登録されました。同じ登録には興福寺、春日大社、春日山原始林などが含まれます。',
          source: 'https://www.city.nara.lg.jp/site/world-heritage/3482.html',
          mapQuery: '東大寺 南大門',
        },
      ],
    },
    {
      id: 'kasuga-taisha',
      name: '春日大社',
      area: '奈良市・夜',
      image: '/assets/scenery/nara/scenes/06_kasuga_taisha.webp',
      hotspots: [
        {
          id: 'kasuga-3000',
          x: 0.46,
          y: 0.44,
          title: 'およそ三千基の灯籠',
          body: '境内には貴族や武士、庶民が長い年月をかけて奉納してきた石灯籠と釣燈籠がおよそ三千基あります。すべてに火をともす「万燈籠」は、二月の節分と八月のお盆のころに行われます。',
          source: 'https://narashikanko.or.jp/spot/detail_10058.html',
          mapQuery: '春日大社 万燈籠',
        },
        {
          id: 'kasuga-768',
          x: 0.74,
          y: 0.34,
          title: '768年の創建と国宝',
          body: '神護景雲2年（768年）に現在の地に社殿が造られたと伝わります。江戸時代に建てられた本社本殿4棟は国宝に指定され、朱塗りの回廊が社殿を取り囲んでいます。',
          source: 'https://www.city.nara.lg.jp/site/world-heritage/88517.html',
          mapQuery: '春日大社 本殿',
        },
        {
          id: 'kasuga-koto',
          x: 0.22,
          y: 0.5,
          title: '古都奈良の文化財の一つ',
          body: '春日大社は世界遺産「古都奈良の文化財」の構成資産です。神が降り立つ山として敬われてきた春日山のふもとに建ち、古くからの配置をほとんど変えずに今日まで伝えられています。',
          source: 'https://www.city.nara.lg.jp/site/world-heritage/88517.html',
          mapQuery: '春日大社 参道 石灯籠',
        },
      ],
    },
    {
      id: 'yoshinoyama',
      name: '吉野山の桜',
      area: '吉野町・春',
      image: '/assets/scenery/nara/scenes/07_yoshinoyama.webp',
      hotspots: [
        {
          id: 'yoshino-30000',
          x: 0.46,
          y: 0.42,
          title: '三万本といわれる山桜',
          body: '吉野山には3万本ともいわれるシロヤマザクラがあり、4月の上旬から中旬にかけて豪華絢爛に咲きみだれます。吉野山は全国に知られた桜の名所です。',
          source:
            'https://www.town.yoshino.nara.jp/promotion/kankojoho/yoshinoyama/sakuranohanashi/1409.html',
          mapQuery: '吉野山 桜',
        },
        {
          id: 'yoshino-senbon',
          x: 0.74,
          y: 0.34,
          title: '下千本から奥千本へ',
          body: '吉野山の桜は、麓から下千本、中千本、上千本、奥千本と呼び分けられます。麓から順に開花し、山を染めていきます。',
          source: 'https://yamatoji.nara-kankou.or.jp/023flower_spot/yoshinoyama/0000000001/',
          mapQuery: '吉野山 中千本',
        },
        {
          id: 'yoshino-2004',
          x: 0.22,
          y: 0.5,
          title: '2004年の世界遺産',
          body: '吉野山は世界遺産「紀伊山地の霊場と参詣道」の一部で、平成16年（2004年）7月1日に登録されました。吉野は修験道と結びついた霊場として、紀伊山地の三つの霊場のひとつに数えられます。',
          source: 'https://www.town.yoshino.nara.jp/promotion/kankojoho/sekaiisan/index.html',
          mapQuery: '吉野山 金峯山寺',
        },
      ],
    },
    {
      id: 'muroji',
      name: '室生寺',
      area: '宇陀市・初夏',
      image: '/assets/scenery/nara/scenes/08_muroji.webp',
      hotspots: [
        {
          id: 'muroji-saisho',
          x: 0.46,
          y: 0.36,
          title: '屋外の古塔で最も小さい',
          body: '奈良県の観光公式サイトは、室生寺の五重塔を「屋外に立つ古塔としてはわが国最小の五重塔」と紹介しています。高さは16メートルで、国宝に指定されています。',
          source: 'https://yamatoji.nara-kankou.or.jp/01shaji/02tera/03east_area/muroji/',
          mapQuery: '室生寺 五重塔',
        },
        {
          id: 'muroji-nyonin',
          x: 0.74,
          y: 0.5,
          title: '女人高野の名',
          body: '高野山が女性の立ち入りを認めなかった時代にも、室生寺は女性の参詣を受け入れたことから「女人高野」と呼ばれました。江戸時代には五代将軍の母・桂昌院の援助を受けています。',
          source: 'https://yamatoji.nara-kankou.or.jp/01shaji/02tera/03east_area/muroji/',
          mapQuery: '室生寺',
        },
        {
          id: 'muroji-shakunage',
          x: 0.22,
          y: 0.48,
          title: '石段を彩る石楠花',
          body: '例年ゴールデンウィークのころ、自然石を敷きつめた石段から五重塔にかけて、道の両側におよそ3000株の石楠花が咲きます。淡いピンクの花が石段を包みます。',
          source: 'https://yamatoji.nara-kankou.or.jp/01shaji/02tera/03east_area/muroji/',
          mapQuery: '室生寺 鎧坂 石楠花',
        },
      ],
    },
    {
      id: 'soni-kogen',
      name: '曽爾高原のススキ',
      area: '曽爾村・秋の夕方',
      image: '/assets/scenery/nara/scenes/09_soni_kogen.webp',
      hotspots: [
        {
          id: 'soni-40ha',
          x: 0.46,
          y: 0.44,
          title: '40ヘクタールのススキ',
          body: '曽爾高原は倶留尊山のふもとに広がる広大な高原です。約40ヘクタールがススキに覆われています。',
          source: 'https://yamatoji.nara-kankou.or.jp/02nature/02kougen/03east_area/sonikogen/',
          mapQuery: '曽爾高原',
        },
        {
          id: 'soni-kin',
          x: 0.74,
          y: 0.34,
          title: '9月に出て11月に金色',
          body: '穂は9月中旬に出はじめ、11月下旬には金色に染まります。日の光を受けると銀色に、夕暮れには金色にと、時間によって高原全体の色が変わります。',
          source: 'https://yamatoji.nara-kankou.or.jp/02nature/02kougen/03east_area/sonikogen/',
          mapQuery: '曽爾高原 ススキ',
        },
        {
          id: 'soni-kameyama',
          x: 0.22,
          y: 0.5,
          title: '山全体が草原の亀山',
          body: '亀山は山全体が草原になっています。ススキの秋だけでなく、新緑の季節も美しい山です。',
          source: 'https://yamatoji.nara-kankou.or.jp/02nature/02kougen/03east_area/sonikogen/',
          mapQuery: '曽爾高原 亀山峠',
        },
      ],
    },
    {
      id: 'inabuchi-tanada',
      name: '稲渕の棚田と石舞台',
      area: '明日香村・秋',
      image: '/assets/scenery/nara/scenes/10_inabuchi_tanada.webp',
      hotspots: [
        {
          id: 'inabuchi-kakashi',
          x: 0.46,
          y: 0.44,
          title: '案山子が並ぶ稲渕の棚田',
          body: '明日香村の稲渕地区では、稲渕棚田案山子祭りが開かれ、棚田の畔に個性豊かな案山子が立ち並びます。稲渕の棚田の周辺は、彼岸花の見どころとしても知られています。',
          source: 'https://yamatoji.nara-kankou.or.jp/023flower_spot/wlt5a4mien/jcae02z286/',
          mapQuery: '稲渕 棚田 明日香村',
        },
        {
          id: 'inabuchi-ishibutai',
          x: 0.74,
          y: 0.34,
          title: '1辺50メートルの方墳',
          body: '明日香村の石舞台古墳は、1辺50メートルの方墳で、6世紀に築かれました。巨石30個を積み上げてつくられた石室で、石の総重量は推定2300トンにのぼります。',
          source:
            'https://yamatoji.nara-kankou.or.jp/03history/04stone/03east_area/ishibutaikofun/',
          mapQuery: '石舞台古墳',
        },
        {
          id: 'inabuchi-hajimari',
          x: 0.22,
          y: 0.5,
          title: '「日本はじまりの中心地」と呼ばれる',
          body: '近くの飛鳥宮跡は、飛鳥観光協会が「日本はじまりの中心地」と紹介する場所です。7世紀、この地域には日本で最初の都が置かれました。2026年7月には「飛鳥・藤原の宮都」が世界文化遺産に登録されています。',
          source: 'https://asukamura.com/',
          mapQuery: '飛鳥宮跡',
        },
      ],
    },
  ],
};
